import { tokenRefreshService } from '../services/tokenRefreshService';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService, Profile } from '../services/apiService';
import { secureOpenAIService } from '../services/secureOpenaiService';
import CognitoAuthService from '../services/cognitoAuthService';
import { sendDebugLog } from '../utils/debugLogging';

interface AuthContextType {
  profile: Profile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<{ needsVerification: boolean; redirectToLogin?: boolean; userExists?: boolean; isVerified?: boolean }>;
  confirmRegistration: (verificationCode: string) => Promise<void>;
  logout: () => void;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAuthenticated: boolean;
  error: string | null;
  clearError: () => void;
  setExternalAuth: (accessToken: string, profile: any) => void;
  needsVerification: boolean;
  pendingRegistration: { email: string; password: string; user?: any } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState<{ email: string; password: string; user?: any } | null>(null);

  const persistProfile = useCallback((nextProfile: Profile | null) => {
    if (nextProfile) {
      localStorage.setItem('profile', JSON.stringify(nextProfile));
      return;
    }

    localStorage.removeItem('profile');
  }, []);

  const hydrateAuthenticatedProfile = useCallback(async (fallbackProfile: Profile) => {
    try {
      const fullProfile = await apiService.getProfile();
      setProfile(fullProfile);
      persistProfile(fullProfile);
      return fullProfile;
    } catch {
      setProfile(fallbackProfile);
      persistProfile(fallbackProfile);
      return fallbackProfile;
    }
  }, [persistProfile]);

  const refreshProfile = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    const hasCookie = document.cookie.includes('authToken');

    if (storedToken && !apiService.getToken()) {
      apiService.setToken(storedToken);
    }

    if (!storedToken && !apiService.getToken() && !hasCookie) {
      return;
    }

    const refreshedProfile = await apiService.getProfile();
    setProfile(refreshedProfile);
    persistProfile(refreshedProfile);
  }, [persistProfile]);

  // Check if user is logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      // #region agent log AUTH_INIT_START
      // Defer logging to avoid React error #300
      setTimeout(() => {
        sendDebugLog({
          sessionId: 'debug-session',
          runId: 'run12',
          hypothesisId: 'AC',
          location: 'AuthContext.tsx:initializeAuth',
          message: 'Auth initialization starting',
          data: {
            hasProfile: !!profile,
          },
          timestamp: Date.now(),
        });
      }, 0);
      // #endregion

      try {
        // Check if there's a potential session before making API call
        const hasToken = localStorage.getItem('token');
        const hasCookie = document.cookie.includes('authToken');
        
        // #region agent log AUTH_TOKEN_CHECK
        // Defer logging to avoid React error #300
        setTimeout(() => {
          sendDebugLog({
            sessionId: 'debug-session',
            runId: 'run12',
            hypothesisId: 'AD',
            location: 'AuthContext.tsx:initializeAuth',
            message: 'Checking for auth token',
            data: {
              hasToken: !!hasToken,
              hasCookie,
            },
            timestamp: Date.now(),
          });
        }, 0);
        // #endregion
        
        // Only make API call if there's a potential session
        if (hasToken || hasCookie) {
          // Verify token by trying to get profile - if this succeeds, token is valid
          try {
            // Load token into apiService if it exists in localStorage
            if (hasToken && !apiService.getToken()) {
              apiService.setToken(hasToken);
            }

            if (hasToken && tokenRefreshService.isTokenExpiredOrExpiringSoon(hasToken)) {
              const refreshed = await tokenRefreshService.forceRefresh();
              if (!refreshed) {
                throw new Error('Authentication required. Please log in again.');
              }
            }
            
            // Wait a bit for token to be fully loaded
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Try to get profile - if this succeeds, token is valid
            const profile = await apiService.getProfile();
            setProfile(profile);
            persistProfile(profile);

            // #region agent log AUTH_PROFILE_FETCH_SUCCESS
            // Defer logging to avoid React error #300
            setTimeout(() => {
              sendDebugLog({
                sessionId: 'debug-session',
                runId: 'run12',
                hypothesisId: 'AF',
                location: 'AuthContext.tsx:initializeAuth',
                message: 'Profile fetched successfully, auth complete',
                data: {
                  hasProfile: !!profile,
                  profileId: profile?.id,
                },
                timestamp: Date.now(),
              });
            }, 0);
            // #endregion
          } catch (tokenError) {
            // Only clear token if it's a 401/403 error (authentication failure)
            // Don't clear on network errors or other issues
            const errorMessage = tokenError instanceof Error ? tokenError.message : String(tokenError);
            const isAuthError = errorMessage.includes('401') || 
                               errorMessage.includes('403') || 
                               errorMessage.includes('Unauthorized') ||
                               errorMessage.includes('Authentication');
            
            if (isAuthError) {
              // CRITICAL: Attempt token refresh before clearing token
              // This handles cases where token expired but can still be refreshed
              try {
                const refreshed = await tokenRefreshService.forceRefresh();
                
                if (refreshed) {
                  // Token refresh succeeded - try to get profile again
                  try {
                    const refreshedProfile = await apiService.getProfile();
                    setProfile(refreshedProfile);
                    persistProfile(refreshedProfile);
                    return; // Success - don't clear token
                  } catch (retryError) {
                    // Profile fetch still failed after token refresh
                  }
                }
              } catch (refreshError) {
                // Token refresh error
              }
              
              // Only clear token if refresh failed or profile fetch still fails after refresh
              localStorage.removeItem('token');
              persistProfile(null);
              secureOpenAIService.clearToken();
              apiService.clearToken();
              setProfile(null);
            } else {
              // Network or other error - keep token, might be temporary
              // Don't clear token on network errors
            }
          }
        }
      } catch (error: unknown) {
        // Handle any other errors
        // Clear any invalid localStorage token and ensure user is logged out
        localStorage.removeItem('token');
        persistProfile(null);
        secureOpenAIService.clearToken();
        setProfile(null);

        // #region agent log AUTH_INIT_ERROR
        // Defer logging to avoid React error #300
        setTimeout(() => {
          sendDebugLog({
            sessionId: 'debug-session',
            runId: 'run12',
            hypothesisId: 'AG',
            location: 'AuthContext.tsx:initializeAuth',
            message: 'Auth initialization error',
            data: {
              errorMessage: error instanceof Error ? error.message : String(error),
            },
            timestamp: Date.now(),
          });
        }, 0);
        // #endregion
      } finally {
        setLoading(false);

        // #region agent log AUTH_LOADING_COMPLETE
        // Defer logging to avoid React error #300
        setTimeout(() => {
          sendDebugLog({
            sessionId: 'debug-session',
            runId: 'run12',
            hypothesisId: 'AH',
            location: 'AuthContext.tsx:initializeAuth',
            message: 'Setting auth loading to false',
            data: {
              hasProfile: !!profile,
              isAuthenticated: !!profile,
            },
            timestamp: Date.now(),
          });
        }, 0);
        // #endregion
      }
    };

    initializeAuth();
    
    // Listen for token refresh events
    // IMPORTANT: Never overwrite profile state with token claim payload.
    // Token payload only has basic Cognito fields and can clobber saved profile fields
    // (phone/linkedin/industry or recently updated name values) until the next API fetch.
    const handleTokenRefreshed = async () => {
      try {
        await refreshProfile();
      } catch {
        // Keep existing profile state if refresh-time profile fetch fails.
      }
    };

    window.addEventListener("tokenRefreshed", handleTokenRefreshed as EventListener);

    return () => {
      window.removeEventListener("tokenRefreshed", handleTokenRefreshed as EventListener);
      // Stop token refresh service on unmount
      tokenRefreshService.stopMonitoring();
    };
  }, [hydrateAuthenticatedProfile, persistProfile, refreshProfile]);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      // Clear any existing tokens before attempting login
      localStorage.removeItem('token');
      secureOpenAIService.clearToken();
      apiService.clearToken();
      setProfile(null);
      
      const authResult = await CognitoAuthService.login(email, password);
      
      const { accessToken, idToken, profile } = authResult;
      
      // Validate that we have tokens
      if (!idToken && !accessToken) {
        throw new Error('No tokens received from Cognito login');
      }
      
      // API Gateway Cognito User Pool authorizer requires id_token, not access_token
      const token_for_api = idToken || accessToken; // Use id_token for API Gateway, fallback to access_token
      
      if (!token_for_api) {
        throw new Error('No token available for API calls');
      }
      
      // Store token in localStorage for backward compatibility
      // API Gateway Cognito authorizer requires id_token
      try {
        localStorage.setItem('token', token_for_api);
      } catch (storageError) {
        throw new Error('Failed to store authentication token');
      }
      
      // Update token in secureOpenAIService
      secureOpenAIService.setToken(token_for_api); // Use id_token for API Gateway (same as apiService)
      // Update token in apiService - use id_token for API Gateway
      apiService.setToken(token_for_api);
      await hydrateAuthenticatedProfile(profile);
      
      // Verify token was actually stored
      const verifyToken = localStorage.getItem('token');
      const verifyApiToken = apiService.getToken();
      
      if (!verifyToken || !verifyApiToken) {
        // Token storage verification failed
      }
    } catch (error: unknown) {
      
      // Ensure we clear any stale tokens on login failure
      localStorage.removeItem('token');
      secureOpenAIService.clearToken();
      apiService.clearToken();
      setProfile(null);
      
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      
      // Preserve error codes when re-throwing (for unconfirmed user detection)
      if (error instanceof Error) {
        const enhancedError = new Error(error.message);
        if ((error as any).errorCode) {
          (enhancedError as any).errorCode = (error as any).errorCode;
        }
        if ((error as any).code) {
          (enhancedError as any).code = (error as any).code;
        }
        if ((error as any).name) {
          (enhancedError as any).name = (error as any).name;
        }
        if ((error as any).field) {
          (enhancedError as any).field = (error as any).field;
        }
        throw enhancedError;
      }
      throw error;
      } finally {
        setLoading(false);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string): Promise<{ needsVerification: boolean; redirectToLogin?: boolean; userExists?: boolean; isVerified?: boolean }> => {
    try {
      setError(null);
      const registrationResult = await CognitoAuthService.register(email, password, firstName, lastName);
      
      // Handle different registration scenarios
      if (registrationResult.redirectToLogin) {
        // User exists and is verified - auto-login and set profile
        const { accessToken, idToken, profile } = registrationResult.authResult!;
        // API Gateway Cognito User Pool authorizer requires id_token, not access_token
        const token_for_api = idToken || accessToken;
        
        // Store token in localStorage for backward compatibility
        // API Gateway Cognito authorizer requires id_token
        localStorage.setItem('token', token_for_api);
        // Update token in secureOpenAIService
        secureOpenAIService.setToken(token_for_api); // Use id_token for API Gateway (same as apiService)
        // Update token in apiService - use id_token for API Gateway
        apiService.setToken(token_for_api);
        await hydrateAuthenticatedProfile(profile);
        
        return { 
          needsVerification: false, 
          redirectToLogin: true, 
          userExists: true, 
          isVerified: true 
        };
      }
      
      if (registrationResult.needsVerification) {
        // CRITICAL: Save to sessionStorage so AuthForm can show verification form
        // This persists across re-renders and page navigation
        const verificationData = {
          email: registrationResult.email!,
          timestamp: new Date().toISOString()
        };
        
        try {
          sessionStorage.setItem('kairos_pending_verification', JSON.stringify(verificationData));
        } catch (error) {
          // Silently handle sessionStorage errors
        }
        
        setNeedsVerification(true);
        setPendingRegistration({
          email: registrationResult.email!,
          password: registrationResult.password!,
          user: registrationResult.user || undefined
        });
        return { 
          needsVerification: true, 
          userExists: registrationResult.userExists || false,
          isVerified: false 
        };
      }
      
      // If we get here, registration was successful without verification
      const { accessToken, idToken, profile } = registrationResult.authResult!;
      // API Gateway Cognito User Pool authorizer requires id_token, not access_token
      const token_for_api = idToken || accessToken;
      
      // Store token in localStorage for backward compatibility
      // API Gateway Cognito authorizer requires id_token
      localStorage.setItem('token', token_for_api);
      // Update token in secureOpenAIService
      secureOpenAIService.setToken(token_for_api); // Use id_token for API Gateway (same as apiService)
      // Update token in apiService - use id_token for API Gateway
      apiService.setToken(token_for_api);
      await hydrateAuthenticatedProfile(profile);
      return { needsVerification: false };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      
      // Preserve error codes when re-throwing
      if (error instanceof Error) {
        const enhancedError = new Error(error.message);
        if ((error as any).errorCode) {
          (enhancedError as any).errorCode = (error as any).errorCode;
        }
        if ((error as any).field) {
          (enhancedError as any).field = (error as any).field;
        }
        throw enhancedError;
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const confirmRegistration = async (verificationCode: string) => {
    if (!pendingRegistration) {
      throw new Error('No pending registration to confirm');
    }

    try {
      setError(null);
      setLoading(true);
      
      // Use our API endpoint to verify email (creates Stripe customer)
      const verifyResponse = await apiService.verifyEmail(
        pendingRegistration.email,
        verificationCode
      );
      
      // After verification, login the user to get tokens
      let authResult: any;
      authResult = await CognitoAuthService.login(
        pendingRegistration.email,
        pendingRegistration.password
      );
      
      const { accessToken, idToken, profile: loginProfile } = authResult;
      // Use profile from verify response if available, otherwise use login profile
      const profile = verifyResponse.profile || loginProfile;
      // API Gateway Cognito User Pool authorizer requires id_token, not access_token
      const token_for_api = idToken || accessToken;
      
      // Store token in localStorage for backward compatibility
      // API Gateway Cognito authorizer requires id_token
      localStorage.setItem('token', token_for_api);
      // Update token in secureOpenAIService
      secureOpenAIService.setToken(token_for_api); // Use id_token for API Gateway (same as apiService)
      // Update token in apiService - use id_token for API Gateway
      apiService.setToken(token_for_api);
      await hydrateAuthenticatedProfile(profile);
      
      // Clear verification state
      setNeedsVerification(false);
      setPendingRegistration(null);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Registration confirmation failed';
      setError(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear server-side cookie
      await CognitoAuthService.logout();
    } catch (error) {
      // Silently handle logout errors
    } finally {
      // Clear localStorage and local state completely
      localStorage.removeItem('token');
      persistProfile(null);
      // Clear token from secureOpenAIService
      secureOpenAIService.clearToken();
      setProfile(null);
      setError(null);
      setLoading(false);
      // Stop token refresh service
      tokenRefreshService.stopMonitoring();
      // Redirect to login page immediately after logout
      window.location.href = '/login';
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!profile) {
      throw new Error('No profile to update');
    }

    try {
      setError(null);
      const updatedProfile = await apiService.updateProfile(updates);
      setProfile(updatedProfile);
      persistProfile(updatedProfile);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      setError(errorMessage);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!profile) {
      throw new Error('No profile to update');
    }

    try {
      setError(null);
      await apiService.changePassword(currentPassword, newPassword);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Password change failed';
      setError(errorMessage);
      throw error;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const setExternalAuth = (accessToken: string, profileData: any) => {
    // Store token in localStorage
    localStorage.setItem('token', accessToken);
    
    // Update token in secureOpenAIService
    secureOpenAIService.setToken(accessToken);
    // Update token in apiService
    apiService.setToken(accessToken);
    
    // Convert profile data to match expected Profile interface
    const profile: Profile = {
      id: profileData.id,
      email: profileData.email,
      firstName:
        profileData.first_name ||
        profileData.firstName ||
        profileData.full_name?.split(' ')[0] ||
        '',
      lastName:
        profileData.last_name ||
        profileData.lastName ||
        profileData.full_name?.split(' ').slice(1).join(' ') ||
        '',
      isActive: profileData.status === 'active',
      emailVerified: profileData.email_verified || false,
      createdAt: new Date().toISOString(), // Use current time as fallback
      updatedAt: new Date().toISOString()
    };
    
    setProfile(profile);
    persistProfile(profile);
    setError(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      profile,
      loading,
      login,
      register,
      confirmRegistration,
      logout,
      updateProfile,
      changePassword,
      refreshProfile,
      isAuthenticated: !!profile,
      error,
      clearError,
      setExternalAuth,
      needsVerification,
      pendingRegistration
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
